import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from '@utilitywarehouse/hearth-react';
import type { ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';

interface HearthAccordionProps {
  question: ReactNode;
  answer: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  scrollOnToggle?: boolean;
  className?: string;
  style?: React.CSSProperties;
  triggerStyle?:
    | React.CSSProperties
    | ((isOpen: boolean) => React.CSSProperties);
}

const HearthAccordion = ({
  question,
  answer,
  onToggle,
  scrollOnToggle = false,
  className,
  style,
  triggerStyle,
}: HearthAccordionProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState<string>('');

  const handleValueChange = (newValue: string) => {
    const isExpanded = newValue !== '';
    setValue(newValue);
    if (onToggle) {
      onToggle(isExpanded);
    }
  };

  useEffect(() => {
    // Defer scrollIntoView until after render
    const timer = setTimeout(() => {
      if (scrollOnToggle && value !== '' && contentRef.current) {
        contentRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [value, scrollOnToggle]);

  return (
    <Accordion
      type="single"
      collapsible
      value={value}
      onValueChange={handleValueChange}
      className={className}
      style={style}
    >
      <AccordionItem value="accordion-item">
        <AccordionHeader>
          <AccordionTrigger
            style={
              typeof triggerStyle === 'function'
                ? triggerStyle(value !== '')
                : triggerStyle
            }
          >
            {question}
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          <div ref={contentRef}>{answer}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default HearthAccordion;
